import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { COLORS } from './pipelineStatus';

// Custom props (segmentColor, failed, muted) aren't valid HTML attributes, so styled-components v4
// doesn't forward them to the DOM.
const Bar = styled.div`
  display: flex;
  gap: 2px;
  width: 140px;
  height: 14px;
  padding: 2px;
  border: 1px solid #e1e5eb;
  border-radius: 3px;
  background: #fff;
  opacity: ${({ muted }) => (muted ? 0.5 : 1)};
`;

const Segment = styled.div`
  flex: 1;
  min-width: 3px;
  background: ${({ segmentColor }) => segmentColor};
  border-radius: 1px;
  ${({ failed }) => failed && `border: 1px dashed ${COLORS.fail};`}
`;

// Fixed left-to-right order regardless of how the segments were produced, so the bar reads the
// same way every time instead of shuffling with whatever order the splits/payments happened to
// come in.
const COLOR_ORDER = [COLORS.ok, COLORS.warning, COLORS.fail, COLORS.pending];

// Many splits/payments used to mean one Segment each, each with its own `min-width: 3px` — past
// ~47 of them that minimum stops fitting inside the fixed 140px bar and flexbox grows the
// container instead of shrinking the segments. Grouping by color caps it at 4 blocks, each sized
// proportionally to how many segments it represents (via flex-grow, so the bar's total width
// never changes), with the full list of titles for that color in its tooltip.
const groupByColor = (segments) => {
  const byColor = segments.reduce((memo, item) => {
    memo[item.color] = memo[item.color] || [];
    memo[item.color].push(item);
    return memo;
  }, {});

  return COLOR_ORDER.filter((color) => byColor[color]?.length).map((color) => ({
    color,
    items: byColor[color]
  }));
};

// `segments === null` means the data is still loading; `error` means the source failed.
export default function PipelineBar({ segments, error }) {
  if (error) {
    return (
      <Bar title={error}>
        <Segment segmentColor="transparent" failed />
      </Bar>
    );
  }
  if (segments === null) {
    return (
      <Bar muted title="Cargando...">
        <Segment segmentColor="#f0f2f5" />
      </Bar>
    );
  }
  const summary = segments.map((item) => item.title).join('\n');
  const groups = groupByColor(segments);
  return (
    <Bar title={summary}>
      {groups.map((group) => (
        <Segment
          key={group.color}
          segmentColor={group.color}
          style={{ flex: `${group.items.length} 0 0px` }}
          title={group.items.map((item) => item.title).join('\n')}
        />
      ))}
    </Bar>
  );
}

PipelineBar.propTypes = {
  error: PropTypes.string,
  segments: PropTypes.arrayOf(
    PropTypes.shape({ color: PropTypes.string.isRequired, title: PropTypes.string })
  )
};
